import { Box, Stack } from '@mui/material';
import { useDrawer } from 'components/drawers/inc-drawer/inc-drawer.hooks';
import SearchToolbar from 'components/search/search-toolbar/search-toolbar.component';
import {
  MultipleSearchQueryProvider,
  SearchParamsProvider,
} from 'components/search/search.context';
import {
  useSearchParams,
  useSearchQuery,
} from 'components/search/search.hooks';
import JobsStatusGate from 'components/jobs-status-gate/jobs-status-gate.component';
import { useOverviewSearchParams } from '../email-pulse.hooks';
import EmailPulseHeader from '../email-pulse-header/email-pulse-header.component';
import OverviewSummaryCards from './overview-summary-cards.component';
import ApiDetailsDrawer from '../email-pulse-api-definitions/api-details-drawer.component';
import {
  useGetEmailPulseOverviewSummaryQuery,
  useGetEmailPulseEmailsOpenedBreakdownQuery,
  useGetEmailPulseEmailsBouncedBreakdownQuery,
  useGetEmailPulseOverviewTrendsQuery,
} from 'api/reports-service/email-pulse-hooks';
import IncLineChart from 'components/charts/inc-line-chart/inc-line-chart.component';
import { useChartDataWithTranslations } from 'components/charts/inc-charts.hooks';
import { dateXAxis } from '../email-pulse.utils';
import { useTranslation } from 'react-i18next';

const filteredSearchParams = ['count', 'page', 'emailType'];

const SearchParamsWrapper = () => {
  const searchParams = useSearchParams(useOverviewSearchParams());
  return (
    <SearchParamsProvider value={searchParams}>
      <Overview />
    </SearchParamsProvider>
  );
};

const Overview = () => {
  const { t } = useTranslation();
  const { open, close, isOpen } = useDrawer();

  const overviewSummary = useSearchQuery(useGetEmailPulseOverviewSummaryQuery, {
    filteredSearchParams,
  });

  const emailsOpenedBreakdown = useSearchQuery(
    useGetEmailPulseEmailsOpenedBreakdownQuery,
    {
      filteredSearchParams,
    },
  );

  const emailsBouncedBreakdown = useSearchQuery(
    useGetEmailPulseEmailsBouncedBreakdownQuery,
    {
      filteredSearchParams,
    },
  );

  const overviewTrends = useSearchQuery(useGetEmailPulseOverviewTrendsQuery, {
    filteredSearchParams,
  });

  // Apply translations to chart series
  const trendsSeries = useChartDataWithTranslations(
    overviewTrends.data?.series,
    label => `page.emailPulse.${label}.label`,
  );

  return (
    <Stack marginTop={2}>
      <MultipleSearchQueryProvider
        value={{
          queries: {
            overviewSummary,
            emailsOpenedBreakdown,
            emailsBouncedBreakdown,
            overviewTrends,
          },
        }}
      >
        <Stack spacing={2}>
          <SearchToolbar />
          <EmailPulseHeader open={open} />

          <Box display="grid" gap={2}>
            {/* Summary Cards */}
            <OverviewSummaryCards />

            {/* Trends Chart */}
            <Box display="grid" gridAutoRows={{ xs: 500 }}>
              <IncLineChart
                data-testid="Overview.TrendsChart"
                title={t('page.emailPulse.trends.title')}
                series={trendsSeries}
                LineChartProps={{
                  loading: overviewTrends.queryState.isFetching,
                  xAxis: [dateXAxis(overviewTrends.data?.xlabels ?? [])],
                  sx: {
                    height: 450,
                  },
                }}
              />
            </Box>
          </Box>
        </Stack>
      </MultipleSearchQueryProvider>
      <ApiDetailsDrawer isOpen={isOpen} close={close} />
    </Stack>
  );
};

export default function GatedPage() {
  return (
    <JobsStatusGate>
      <SearchParamsWrapper />
    </JobsStatusGate>
  );
}
